// @flow

import React, { useState, useEffect, useRef } from 'react'
import { useOverflow } from '../../../hooks'
import { useTranslation } from 'react-i18next'

import Modal from '../../Modal'
import ModalBody from '../../Modal/ModalBody'
import ModalHead from '../../Modal/ModalHead'
import ModalCloseButton from '../../Modal/ModalCloseButton'
import ModalButtons from '../../Modal/ModalButtons'
import ModalLabel from '../../Modal/ModalLabel'

import NewSelectSimple from '../../NewSelectSimple'
import Checkbox from '../../Checkbox'
import Button from '../../Button'
import Textarea from 'react-textarea-autosize'
import Loader from '../../Loader'

import BrowserStorage from '../../../utils/browserStorage'

import { newLineToBr } from '../../../utils/text'

import StarsIcon from '../../../../static/icons/ai-stars-big.svg'

import {
  generateAIEmailMessage,
  getAIEmailPrompts,
} from '../../../core/api/api.mail'

import styles from './GenerateAIEmailModal.module.scss'

import type { Node } from 'react'

type Props = {
  isOpen: boolean,
  onClose: () => void,
  onCopy: (string, string) => void,
  requestId: number,
  threadId: string,
}

const GenerateAIEmailModal = (props: Props): Node => {
  const { requestId, threadId } = props

  useOverflow()

  const { t } = useTranslation('Mail')

  const timerRef = useRef()

  const [promptOptions, setPromptOptions] = useState([])
  const [init, setInit] = useState(false)
  const otherOption = {
    value: -1,
    label: t('CustomPrompt'),
  }

  useEffect(() => {
    getAIEmailPrompts().then(data => {
      setPromptOptions([
        otherOption,
        ...data.results.objects.map(o => ({
          label: o.title,
          value: o.text,
        })),
      ])
    })

    return () => {
      clearTimeout(timerRef.current)
    }
  }, [])

  const [message, setMessage] = useState('')
  const [description, setDescription] = useState('')
  const [subject, setSubject] = useState('')
  const [confirmCb, setConfirmCb] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [onceGenerated, setOnceGenerated] = useState(false)
  const [error, setError] = useState(null)

  const tones = [
    'formal',
    'informal',
    'friendly',
    'professional',
    'apologetic',
    'persuasive',
  ]
  const lengths = ['short', 'medium', 'long']

  const MAX_REQUEST_ATTEMPTS = 5

  const toneOptions = tones.map(o => ({
    value: o,
    label: t(`Tone.${o}`),
  }))

  const lengthOptions = lengths.map(o => ({
    value: o,
    label: t(`Length.${o}`),
  }))

  const [promptTextOption, setPromptTextOption] = useState(otherOption)
  const [toneOption, setToneOption] = useState(toneOptions[0])
  const [lengthOption, setLengthOption] = useState(lengthOptions[0])

  useEffect(() => {
    setOnceGenerated(false)
  }, [toneOption, lengthOption, description])

  useEffect(() => {
    if (!init) {
      setDescription(
        BrowserStorage.get(`email_prompt_${requestId}_${threadId}`)
      )
      setInit(true)
    } else if (promptTextOption?.value) {
      setDescription(
        promptTextOption.value === -1 ? '' : promptTextOption.value
      )
    }
  }, [promptTextOption])

  const handleChangeConfirm = e => setConfirmCb(e.currentTarget.checked)

  const handleGenerateMessage = () => {
    const prompt_text = description
    const tone = toneOption.value
    const length = lengthOption.value
    setGenerating(true)
    setError(null)
    generateAIEmailMessage({
      prompt_text,
      tone,
      length,
    })
      .then(data => {
        setOnceGenerated(true)
        setMessage(newLineToBr(data.body))
        setSubject(data.subject)

        if (description) {
          BrowserStorage.set(
            `email_prompt_${requestId}_${threadId}`,
            description
          )
        }
      })
      .catch(err => {
        if (err.message?.response?.status === 429) {
          setError(t('MaxAttemptsWarning', { max: MAX_REQUEST_ATTEMPTS }))
        } else {
          setError(t('ErrorGeneratingAIMessage'))
        }

        setOnceGenerated(false)
      })
      .finally(() => setGenerating(false))
  }

  const handleCopyMessage = () => {
    props.onCopy(message, subject)
    props.onClose()
  }

  return (
    <Modal
      isOpen={props.isOpen}
      className={styles.modal}
      onRequestClose={props.onClose}
    >
      <ModalHead
        showAlt
        title={t('GenerateAIEmailTitle')}
        className={styles.modalHead}
      />
      <ModalCloseButton onClose={props.onClose} />
      <ModalBody className={styles.modalBody}>
        <div className={styles.column}>
          <ModalLabel className={styles.label}>{t('Prompt')}</ModalLabel>
          <NewSelectSimple
            useSearch
            className={styles.select}
            value={promptTextOption}
            options={promptOptions}
            placeholder={t('ChoosePrompt')}
            onChange={setPromptTextOption}
          />
          <ModalLabel className={styles.label}>{t('DescribeEmail')}</ModalLabel>
          <Textarea
            className={styles.textarea}
            value={description}
            name={'describe-email'}
            placeholder={t('EnterEmailDescription')}
            onChange={e => setDescription(e.target.value)}
          />
          <ModalLabel className={styles.label}>{t('EmailTone')}</ModalLabel>
          <NewSelectSimple
            className={styles.select}
            value={toneOption}
            options={toneOptions}
            onChange={setToneOption}
          />
          <ModalLabel className={styles.label}>{t('EmailLength')}</ModalLabel>
          <NewSelectSimple
            className={styles.select}
            value={lengthOption}
            options={lengthOptions}
            onChange={setLengthOption}
          />
          <Checkbox
            checked={confirmCb}
            text={t('GenerateConfirm')}
            style={{ marginTop: '12px' }}
            onChange={handleChangeConfirm}
          />
        </div>
        <div className={styles.column}>
          {generating ? (
            <Loader className={styles.loader} text={false} type='big' />
          ) : message ? (
            <div className={styles.message}>
              <p>
                <b>{subject}</b>
              </p>
              <div dangerouslySetInnerHTML={{ __html: message }} />
            </div>
          ) : (
            <div className={styles.placeholder}>
              <StarsIcon />
              <span>{t('NoSuggetion')}</span>
            </div>
          )}
        </div>
      </ModalBody>
      <ModalButtons>
        {!onceGenerated && (
          <Button.Regular
            className={styles.aibutton}
            disabled={!confirmCb || !description || generating}
            icon='aistick'
            onClick={handleGenerateMessage}
          >
            {t('Generate')}
          </Button.Regular>
        )}
        {onceGenerated && (
          <Button.Save disabled={generating} onClick={handleCopyMessage}>
            {t('CopyMessage')}
          </Button.Save>
        )}
        <Button.Cancel onClick={props.onClose}>
          {t('Common:Cancel')}
        </Button.Cancel>
        {onceGenerated && (
          <Button.Text
            className={styles.regenerate}
            icon='updates'
            title={t('Regenerate')}
            onClick={handleGenerateMessage}
          />
        )}
        {error && <span className={styles.warning}>{error}</span>}
      </ModalButtons>
    </Modal>
  )
}

export default GenerateAIEmailModal
